import {
  ForgeButton,
  ForgeDialog,
  ForgeIcon,
  ForgeIconButton,
  ForgePopover,
  ForgeScaffold,
  ForgeToolbar,
  ForgeTooltip,
} from '@tylertech/forge-react';
import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import styles from './AdvancedTotalingModal.module.scss';
import { useEscapeKey } from 'common/visualizations/views/agGridReact/customHooks/useEscapeKey';
import { IDialogBeforeCloseEventData } from '@tylertech/forge';
import EventBus from 'common/visualizations/views/agGridReact/helpers/EventBus';
import { I18n } from 'common/i18n';
import { HierarchyColumnConfig, Vif } from 'common/visualizations/vif';
import { useDispatch } from 'react-redux';
import { applyAdvancedTotalingConfig } from 'common/authoring_workflow/actions';
import RowGroupSelect from './RowGroupSelect';
import CustomSubtotalLabelField from './CustomSubtotalLabelField';
import useColumnGroupings from './customHooks/useColumnGroupings';
import { useAutocompleteValues } from './customHooks/useAutocompleteValues';
import { AdvancedFormulaEditor } from './AdvancedFormulaEditor';
import { useAutocompleteHighlighting } from 'common/components/FormulaEditor/customHooks/useAutocompleteHighlighting';
import { unwrapFormulaTerms, extractFormulaTerms, wrapFormulaTerms } from './helpers/formulaHelpers';
import classNames from 'classnames';

interface AdvancedTotalingModalProps {
  isTableAdvancedTotalingModalOpen: boolean;
  advancedTotalingModalTitle: string;
  advancedTotalingModalHierarchyId: string;
  vif: Vif;
}

const scope = 'shared.components.advanced_totaling.modal';

const AdvancedTotalingModal: React.FC<AdvancedTotalingModalProps> = ({
  isTableAdvancedTotalingModalOpen,
  advancedTotalingModalTitle,
  advancedTotalingModalHierarchyId,
  vif,
}: AdvancedTotalingModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { columnWithAdvancedTotalingConfig, groupingColumnNames, hierarchyIndex, selectedColumnGroupings } =
    useColumnGroupings(vif, advancedTotalingModalHierarchyId);

  // State for custom formulas (not saved to VIF until Apply is clicked)
  const [customFormula, setCustomFormula] = useState<string>('');

  // State for the row group dropdown
  const [currentRowGroup, setCurrentRowGroup] = useState(columnWithAdvancedTotalingConfig?.columnName || '');

  const [selectedColumn, setSelectedColumn] = useState<HierarchyColumnConfig | undefined>(
    columnWithAdvancedTotalingConfig,
  );

  // State for the custom subtotal label field
  const [subtotalLabelFieldValue, setSubtotalLabelFieldValue] = useState<string>(
    selectedColumn?.subtotalLabel || '',
  );

  //selectedAutocompleteValues can be used to track selected values from the autocomplete dropdown
  const { selectedAutocompleteValues, setupHighlightingListeners, syncHighlightedTerms } =
    useAutocompleteHighlighting();

  // Use custom hook to fetch autocomplete values
  const autocompleteValues = useAutocompleteValues(vif, currentRowGroup);

  useEffect(() => {
    if (isTableAdvancedTotalingModalOpen) {
      // Logic to ensure the formula is unwrapped and highlighted when the modal loads
      const currentConfig =
        selectedColumnGroupings.find((c) => c.columnName === currentRowGroup) || selectedColumn;

      const savedFormula = currentConfig?.customFormula || '';

      // Sync the internal highlighting terms before setting the unwrapped formula
      syncHighlightedTerms(extractFormulaTerms(savedFormula));
      setCustomFormula(unwrapFormulaTerms(savedFormula));

      //Initially Set the top-most row group column configuration as the default value
      if (!currentRowGroup && selectedColumnGroupings.length > 0) {
        setCurrentRowGroup(selectedColumnGroupings[0].columnName);
      }
    }
  }, [
    isTableAdvancedTotalingModalOpen,
    currentRowGroup,
    selectedColumnGroupings,
    syncHighlightedTerms,
    selectedColumn,
  ]);

  const wrappedCustomFormula = useMemo(
    () => wrapFormulaTerms(customFormula, selectedAutocompleteValues),
    [customFormula, selectedAutocompleteValues],
  );

  const hasChanges = useMemo(() => {
    const subtotalLabelValueChanged = subtotalLabelFieldValue !== (selectedColumn?.subtotalLabel || '');

    const savedConfig = selectedColumnGroupings.find((c) => c.columnName === currentRowGroup);
    const savedWrapped = savedConfig?.customFormula || '';

    const customFormulaValueChanged = wrappedCustomFormula !== savedWrapped;

    return subtotalLabelValueChanged || customFormulaValueChanged;
  }, [
    subtotalLabelFieldValue,
    selectedColumn,
    wrappedCustomFormula,
    currentRowGroup,
    selectedColumnGroupings,
  ]);

  const handleApplyFormulas = () => {
    dispatch(
      applyAdvancedTotalingConfig(hierarchyIndex, {
        subtotalLabelForColumn: {
          columnName: currentRowGroup,
          subtotalLabel: subtotalLabelFieldValue,
        },
        customFormulas: {
          [currentRowGroup]: wrapFormulaTerms(customFormula, selectedAutocompleteValues),
        },
      }),
    );

    hideModal();
  };

  const hideModal = useCallback(() => {
    EventBus.dispatch('advancedTotaling', {
      advancedTotalingModalTitle: '',
      isTableAdvancedTotalingModalOpen: false,
      advancedTotalingModalHierarchyId: undefined,
    });
  }, []);

  useEscapeKey({
    onKeyup: hideModal,
    enabled: isTableAdvancedTotalingModalOpen,
    eventTarget: modalRef.current || document,
    restoreFocusTarget: '.authoring-modal',
  });

  const modalTitle = useMemo(() => {
    return `${I18n.t('title', { scope })} - ${advancedTotalingModalTitle}`;
  }, [advancedTotalingModalTitle]);

  const onColumnGroupingsSelectChange = useCallback(
    (event: CustomEvent) => {
      // Currently it seems that the index of the rowGroup in `selectedColumnGroupings`
      // match with the row group level. Kindly update this `rowGroupLevel` logic if
      // the row group level changes
      setCurrentRowGroup(event.detail);

      // We would always show the previously saved subtotal label value for the column.
      // If user would apply empty subtotal label on the next row group,
      // it will clear all subtotal labels for all grouped rows (As per MBI0)
      const newSelectedRowGroup = selectedColumnGroupings.find(
        (groupedColumn) => groupedColumn.columnName === event.detail,
      );

      setSelectedColumn(newSelectedRowGroup);

      if (newSelectedRowGroup?.subtotalLabel) {
        setSubtotalLabelFieldValue(newSelectedRowGroup.subtotalLabel);
      } else {
        setSubtotalLabelFieldValue('');
      }

      // Logic to handle highlighting sync and unwrapping when switching groups
      if (newSelectedRowGroup?.customFormula) {
        const formula = newSelectedRowGroup.customFormula;
        syncHighlightedTerms(extractFormulaTerms(formula));
        setCustomFormula(unwrapFormulaTerms(formula));
      } else {
        syncHighlightedTerms([]);
        setCustomFormula('');
      }
    },
    [selectedColumnGroupings, syncHighlightedTerms],
  );

  const onFormulaChange = useCallback(
    (formula: string) => {
      if (currentRowGroup) {
        setCustomFormula(formula);
      }
    },
    [currentRowGroup],
  );

  const renderInfoPopoverContent = () => {
    const operations = ['add', 'subtract', 'multiply', 'divide', 'parentheses'];
    const listElements = operations.map((operation) => {
      return (
        <p className="forge-typography--label1" key={`${operation}`}>
          {I18n.t(`operators.list.${operation}`, { scope })}
        </p>
      );
    });
    return (
      <div className={styles['info-popover-content']} data-testid="info-popover-content">
        <p className="forge-typography--label1">{I18n.t('operators.title', { scope })}</p>
        {listElements}
      </div>
    );
  };

  return (
    <ForgeDialog
      on-forge-dialog-before-close={(e: CustomEvent<IDialogBeforeCloseEventData>) => {
        if (e.detail.reason === 'backdrop') {
          e.preventDefault();
        }
      }}
      on-forge-dialog-close={hideModal}
      open={isTableAdvancedTotalingModalOpen}
      persistent={true}
      ref={modalRef}
      className="advanced-totaling-dialog"
      data-testid="advanced-totaling-dialog"
      id="advanced-totaling-dialog"
    >
      <div
        className={classNames('advanced-totaling-modal', styles['modal-container'])}
        data-testid="advanced-totaling-modal"
      >
        <ForgeScaffold>
          <ForgeToolbar slot={'header'}>
            <div slot="start" className={styles['modal-title-container']}>
              <ForgeTooltip placement={'top'} data-testid="advanced-totaling-modal-title-tooltip">
                {modalTitle}
              </ForgeTooltip>
              <h1 className="forge-typography--heading4" data-testid="advanced-totaling-modal-title">
                {modalTitle}
              </h1>
            </div>
            <ForgeIconButton slot={'end'} onClick={hideModal} data-testid="close-btn">
              <ForgeIcon name={'close'}></ForgeIcon>
            </ForgeIconButton>
          </ForgeToolbar>
          <div className={styles['modal-body']} data-testid="advanced-totaling-modal-body" slot="body">
            <div className={styles.dropdown}>
              <RowGroupSelect
                {...{
                  currentRowGroupValue: currentRowGroup,
                  groupingColumnNames,
                  selectedColumnGroupings,
                  onChange: onColumnGroupingsSelectChange,
                }}
              />
              <div className={styles['info-popover-container']}>
                <ForgeIcon name={'info_outline'} data-testid={'info-popover'} id={'info-popover'}></ForgeIcon>
                <ForgePopover placement="bottom" trigger-type="hover" anchor={'info-popover'}>
                  {renderInfoPopoverContent()}
                </ForgePopover>
              </div>
            </div>
            {currentRowGroup && (
              <div
                className={styles['advanced-totaling-modal-formula-editor']}
                data-testid="formula-editor-container"
              >
                <AdvancedFormulaEditor
                  autocompleteValues={autocompleteValues}
                  formula={customFormula || ''}
                  onFormulaChange={onFormulaChange}
                  setupHighlightingListeners={setupHighlightingListeners}
                />
              </div>
            )}
            <div className={styles['custom-subtotal-label-field']}>
              <CustomSubtotalLabelField
                onChange={setSubtotalLabelFieldValue}
                initialValue={subtotalLabelFieldValue}
              />
            </div>
          </div>
          <ForgeToolbar slot={'footer'}>
            <ForgeButton
              slot={'start'}
              // onClick={handleClearAllFormulas}
              // disabled={!hasFormulas}
              data-testid="clear-all-formulas-btn"
            >
              {I18n.t('clear_all_formulas', { scope })}
            </ForgeButton>
            <ForgeButton
              slot={'end'}
              variant={'raised'}
              data-testid="apply-btn"
              onClick={handleApplyFormulas}
              disabled={!hasChanges}
            >
              {I18n.t('apply', { scope })}
            </ForgeButton>
          </ForgeToolbar>
        </ForgeScaffold>
      </div>
    </ForgeDialog>
  );
};

export default AdvancedTotalingModal;
